import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/ui';
import type { KanbanActivity, KanbanBoard } from '../types';
import type { UITranslations } from '../i18n/translations';
import { updateActivity, addComment, deleteActivity } from '../services/api';

interface KanbanActivityModalProps {
  activity: KanbanActivity;
  board: KanbanBoard;
  roomId: string;
  isAdmin: boolean;
  userId: string | null;
  userName: string | null;
  t: UITranslations['room'];
  onClose: () => void;
  onBoardUpdate: (board: KanbanBoard) => void;
  onActivityUpdate: (activity: KanbanActivity) => void;
}

export const KanbanActivityModal = ({
  activity,
  board,
  roomId,
  isAdmin,
  userId,
  userName,
  t,
  onClose,
  onBoardUpdate,
  onActivityUpdate,
}: KanbanActivityModalProps) => {
  const [title, setTitle] = useState(activity.title);
  const [description, setDescription] = useState(activity.description ?? '');
  const [assignee, setAssignee] = useState(activity.assignee ?? '');
  const [taskType, setTaskType] = useState(activity.type ?? board.taskTypes?.[0] ?? '');
  const [commentText, setCommentText] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'comments'>('details');
  const [saving, setSaving] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedTimerRef = useRef<number | null>(null);

  const currentColumn = board.columns.find((c) => c.id === activity.columnId);
  const historyEntries = activity.movementHistory ?? [];
  const sortedHistory = [...historyEntries].sort((a, b) => b.movedAt - a.movedAt);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) {
        window.clearTimeout(savedTimerRef.current);
        savedTimerRef.current = null;
      }
    };
  }, []);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateActivity(roomId, board.id, activity.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        assignee: assignee.trim() || undefined,
        type: taskType || undefined,
      });
      onActivityUpdate(updated);
      setShowSaved(true);
      if (savedTimerRef.current) {
        window.clearTimeout(savedTimerRef.current);
      }
      savedTimerRef.current = window.setTimeout(() => {
        setShowSaved(false);
        savedTimerRef.current = null;
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !userId) return;
    setAddingComment(true);
    setError(null);
    try {
      const updated = await addComment(roomId, board.id, activity.id, {
        userId,
        userName: userName ?? userId,
        content: commentText.trim(),
      });
      onActivityUpdate(updated);
      setCommentText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t.deleteActivity + '?')) return;
    setDeleting(true);
    try {
      const updatedBoard = await deleteActivity(roomId, board.id, activity.id);
      onBoardUpdate(updatedBoard);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete activity');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-16">
      <div className="relative w-full max-w-lg rounded-2xl border bg-background shadow-2xl">
        {showSaved && (
          <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-lg">
            {t.saveSuccess}
          </div>
        )}
        <div className="flex items-start justify-between border-b px-6 py-3">
          <div className="flex-1">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-transparent text-lg font-semibold focus:outline-none"
            />
            {currentColumn && (
              <p className="mt-0.5 text-xs text-muted-foreground">📋 {currentColumn.name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-2 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="border-b px-6 pb-2 pt-3">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('details')}
              className={`border-b-2 px-2 pb-2 text-sm font-semibold ${
                activeTab === 'details'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground'
              }`}
            >
              {t.activityDescription}
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={`border-b-2 px-2 pb-2 text-sm font-semibold ${
                activeTab === 'comments'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground'
              }`}
            >
              {t.comments}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`border-b-2 px-2 pb-2 text-sm font-semibold ${
                activeTab === 'history'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground'
              }`}
            >
              {t.history}
            </button>
          </div>
        </div>

        {activeTab === 'details' ? (
          <div className="space-y-4 p-6">
            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                {t.activityDescription}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.activityDescriptionPlaceholder}
                rows={3}
                className="w-full resize-none rounded-lg border border-border/80 bg-background/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                {t.assignee}
              </label>
              <input
                type="text"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                placeholder={t.assigneePlaceholder}
                className="h-9 w-full rounded-lg border border-border/80 bg-background/80 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                {t.taskType}
              </label>
              <select
                value={taskType}
                onChange={(e) => setTaskType(e.target.value)}
                className="h-9 w-full rounded-lg border border-border/80 bg-background/80 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">{t.taskTypePlaceholder}</option>
                {(board.taskTypes && board.taskTypes.length > 0
                  ? board.taskTypes
                  : ['Feature', 'Bug', 'Analise']
                ).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={handleSave} disabled={saving || !title.trim()}>
                {saving ? 'Saving...' : t.saveActivity}
              </Button>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-destructive hover:bg-destructive/10"
                >
                  {t.deleteActivity}
                </Button>
              )}
            </div>
          </div>
        ) : activeTab === 'comments' ? (
          <div className="space-y-4 p-6">
            <div className="h-48 space-y-3 overflow-y-auto pr-1">
              {activity.comments.map((comment) => {
                const isOwn = comment.userId === userId;
                return (
                  <div
                    key={comment.id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                        isOwn
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <div className="mb-1 flex items-center justify-between gap-3 text-[11px] opacity-80">
                        <span className="font-semibold">
                          {isOwn ? 'Voce' : comment.userName}
                        </span>
                        <span>{new Date(comment.createdAt).toLocaleString()}</span>
                      </div>
                      <p className="break-words">{comment.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t.commentPlaceholder}
                disabled={addingComment}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddComment();
                }}
                className="h-9 flex-1 rounded-lg border border-border/80 bg-background/80 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
              <Button
                size="sm"
                onClick={handleAddComment}
                disabled={addingComment || !commentText.trim() || !userId}
              >
                {t.addComment}
              </Button>
            </div>
          </div>
        ) : activeTab === 'history' ? (
          <div className="space-y-3 p-6">
            {sortedHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.noHistory}</p>
            ) : (
              <div className="space-y-3">
                {sortedHistory.map((entry) => {
                  const columnName =
                    board.columns.find((c) => c.id === entry.toColumnId)?.name || entry.toColumnId;
                  const movedAt = new Date(entry.movedAt).toLocaleString();
                  return (
                    <div
                      key={`${entry.movedAt}-${entry.toColumnId}`}
                      className="rounded-lg border bg-muted/30 p-3"
                    >
                      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                        <span>{movedAt}</span>
                        <span>{entry.movedByEmail || '-'}</span>
                        <span>{columnName}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
